// src/stores/useQcStore.js
//
// PadRekko Reclamation QC lifecycle. As of Day 1 of the multi-party build,
// the entry point is a CASE (multi-org workflow envelope) that wraps a
// session 1:1. Components downstream of the inspector continue to read
// session-shaped state (`session.pads`, `session.id`) unchanged — the
// case slot is a thin wrapper that powers the header banner + stage
// transition logic only.
//
// Backend endpoints (POST /cases, GET /cases, GET /cases/:id, PATCH
// /cases/:id/stage, plus the migrated /sessions/:id/...  routes) are
// all gated by the PADREKKO entitlement + the new case-access middleware.
// 401/403 means re-login / wrong org / not a participant, not a code bug.
//
// What lives here:
//   - `caseInfo`              currently-loaded case (id, name, session_id,
//                             current_stage, config, stage_history,
//                             owner_org_id, participants[])
//   - `caseRole`              caller's role on this case: 'dh_analyst' |
//                             'customer_analyst' | 'operator_analyst'
//   - `caseWriteable`         boolean — caller can mutate at current_stage
//   - `casesList`             picker list (no pads, just case summary rows)
//   - `session`               currently-loaded session (session row + pads
//                             with aoi_bbox, status, summary, findings[]).
//                             Populated by loadCase alongside caseInfo.
//   - `currentPadIdx`         inspector cursor (hoisted out of local state
//                             so tab switches preserve position)
//   - `drawingPolygon`        true while operator is mid-draw; the findings
//                             layer hides itself so the canvas snapshot
//                             captures only the imagery + the new polygon
//   - `analyzeInflightPadId`  pad currently mid-analyze (single global
//                             slot; v0 operator works one pad at a time)
//   - `reanalyzeInflight`     Set of `${padId}:${idx}` keys — multiple
//                             findings on one pad can be reanalyzing in
//                             parallel without trampling each other
//   - `dismissedFindingKeys`  client-only "addressed" marks (Set of
//                             `${padId}:${idx}`); not persisted, server
//                             still owns canonical state. v1 will move
//                             this server-side.
//   - `zoomToPolygon`         {key, nonce} the findings layer subscribes
//                             to and fits-bounds when nonce changes —
//                             same finding clicked twice still re-fits.

import { create } from "zustand";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { useProjectStore } from "./useProjectStore";

const API = "/api/v1/padrekko/qc";

import { STAGE_WRITER, STAGE_NEXT, STAGE_REVERSE_PREV as STAGE_PREV,
         mayWriteAtStage, writerRoleForCase,
         isResponseDeadlineBasis } from "../../../admin-api/lib/padrekko-qc/caseStages.js";

export function nextStageFor(stage) {
  return STAGE_NEXT[stage] ?? null;
}
export function writerRoleFor(stage) {
  // UNUSED AS OF 2026-09-16 and kept only because it is exported. It answers
  // the TWO-PARTY question and a same-tenant caller must not reach it — use
  // writerRoleForThisCase below, which takes the flag. Left rather than deleted
  // so an external importer does not break silently; it will be dropped when
  // the export surface is next swept.
  return STAGE_WRITER[stage] ?? null;
}

/**
 * The writer for THIS case, same-tenant included.
 *
 * IMPORTED, NOT RE-DERIVED. A same-tenant branch written here would be the
 * eighth copy of a rule the server enforces, and the failure mode is REM-63's:
 * the UI renders a control the server then rejects, which reads to a user as a
 * broken app rather than as a boundary. checkStageTablesSingleSource now has
 * mayWriteAtStage and writerRoleForCase in its NAMES, so a re-declaration here
 * fails the commit.
 */
export function writerRoleForThisCase(stage, sameTenant) {
  return writerRoleForCase(stage, sameTenant === true);
}

/** May `role` write at `stage` on a case with this `sameTenant` flag? */
export function mayWrite(stage, role, sameTenant) {
  return mayWriteAtStage({ stage, role, sameTenant: sameTenant === true });
}
export function previousStageFor(stage) {
  return STAGE_PREV[stage] ?? null;
}

// Phase 0 — customer initial-forward mode: the customer is at customer_review
// before the operator's first QC pass (operator_pass_count === 0). Single
// source of truth shared by CaseBanner (handoff label + mode hint) and any
// other surface, so the two can't disagree about what "pass 0" means. The
// findings UI is NO LONGER gated on this — the customer reviews findings in
// pass 0 — but the banner still uses it to frame the handoff.
export function selectIsCustomerInitialForward(state) {
  return (
    state.caseRole === "customer_analyst" &&
    state.caseInfo?.current_stage === "customer_review" &&
    (state.caseInfo?.operator_pass_count ?? 0) === 0
  );
}

// Phase 2 — count of findings (across loaded pads) still kicked back to DH and
// not removed. Drives the CaseBanner forward-button gate + tooltip: the
// customer can't forward to the operator while any kickback is unresolved
// (the backend enforces the same on customer_review -> operator_review).
export function selectUnresolvedKickbackCount(state) {
  let n = 0;
  for (const p of state.session?.pads ?? []) {
    for (const f of p.findings ?? []) {
      if (f.kicked_back_to_dh && !f.is_removed) n += 1;
    }
  }
  return n;
}

// Phase B reopen targets. DH service-provider chooses one when reopening
// a 'complete' case. UI uses this list for the radio group order;
// 'operator_review' is the default because it's the closest prior stage
// and matches the typical small-fix-before-closeout use case.
export const REOPEN_TARGETS = Object.freeze([
  "operator_review",
  "customer_review",
  "dh_review",
]);

// "Where did the user leave off?" on session load. First pending pad if any,
// else first in_progress, else 0. Used by loadSession to seed currentPadIdx
// so refresh-resume is honest without persisting cursor server-side.
function firstActionablePadIdx(pads) {
  if (!Array.isArray(pads) || pads.length === 0) return 0;
  const pendIdx = pads.findIndex((p) => p.status === "pending");
  if (pendIdx >= 0) return pendIdx;
  const ipIdx = pads.findIndex((p) => p.status === "in_progress");
  if (ipIdx >= 0) return ipIdx;
  return 0;
}

// Next finding idx for a given pad. Server's analyze endpoint UPSERTs on
// (pad_id, idx) and accepts operator-assigned idx values, so the client owns
// numbering. max(existing) + 1, starting at 0 for the first draw.
function nextFindingIdx(pad) {
  const existing = pad?.findings ?? [];
  if (existing.length === 0) return 0;
  return Math.max(...existing.map((f) => f.idx)) + 1;
}

// Server's initial history entry for a freshly-created finding, mirrored
// client-side so the card history rail renders identically pre/post-reload.
// Server uses its own clock; ours can drift by ms — acceptable, loadSession
// re-pulls authoritative state.
function createdHistoryEntry() {
  return { event: "created", at: new Date().toISOString(), source: "analyze" };
}

// REM-576 — a refusal in the caller's hands, never blank (REM-629's lesson).
// `error` and `detail` are the server's own words when the body has them; a
// body without them (a proxy's 502, an empty 500) still says which act failed
// and how. `data` is the parsed body, or {} when it did not parse.
function actionFailure(res, data, action) {
  const text = (v) => (typeof v === "string" && v.trim() ? v : null);
  return {
    ok: false,
    status: res.status,
    error: text(data?.error) ?? `${action} failed (HTTP ${res.status})`,
    detail: text(data?.detail),
  };
}

// The request never got an answer (offline, DNS, CORS). Status 0, as fetch
// reports it.
function actionThrew(err, action) {
  const msg = typeof err?.message === "string" && err.message.trim() ? err.message : null;
  return {
    ok: false,
    status: 0,
    error: msg ? `${action} failed: ${msg}` : `${action} failed (network error)`,
    detail: null,
  };
}

// Stable key for a finding across the app: padId scopes it (idx alone
// collides across pads). Used by reanalyzeInflight, dismissedFindingKeys,
// and zoomToPolygon.
export function findingKey(padId, idx) {
  return `${padId}:${idx}`;
}

// Attachment selectors over the flat `attachments` array. Finding-level rows
// carry finding_id; pad-level rows have finding_id null (general pad context).
export function attachmentsForFinding(attachments, findingId) {
  if (!findingId) return [];
  return (attachments ?? []).filter((a) => a.finding_id === findingId);
}
export function attachmentsForPad(attachments, padId) {
  return (attachments ?? []).filter((a) => a.pad_id === padId && !a.finding_id);
}

// User-scoped initial values. Used by _clearUserScoped() on user-switch
// to flush previous-user data (cases list, active case, notes, in-flight
// flags, etc.) so the next user doesn't inherit visibility of records
// they're not entitled to. Tool/UI fields that don't depend on user
// identity (e.g. drawingPolygon) are reset too for cleanliness.
const USER_SCOPED_INITIAL = {
  caseInfo: null,
  caseRole: null,
  caseWriteable: false,
  advanceStageInflight: false,
  addParticipantInflight: false,
  reverseStageInflight: false,
  clearDisputeInflightFindingId: null,
  // Single-flight: one validation at a time, so a double-click cannot race
  // two requests whose responses arrive out of order.
  validateInflightKey: null,
  kickbackInflightFindingId: null,
  resubmitInflightFindingId: null,
  reopenInflight: false,
  closeInflight: false,
  sendBackToOperatorInflight: false,
  releaseInflight: false,
  shareInflight: false,
  revokeShareInflight: false,
  session: null,
  currentPadIdx: null,
  loading: false,
  error: null,
  drawingPolygon: false,
  analyzeInflightPadId: null,
  skipInflightPadId: null,
  finishInflightPadId: null,
  exportInflight: false,
  reanalyzeInflight: new Set(),
  dismissedFindingKeys: new Set(),
  hiddenFindingKeys: new Set(),
  zoomToPolygon: null,
  openCorrectFindingKey: null,
  polygonEditMode: null,
  submitPolygonInflight: false,
  notesByCase: new Map(),
  caseNoteInflight: false,
  findingNoteInflightFindingId: null,
  editNoteInflightId: null,
  deleteNoteInflightId: null,
  // PadRekko attachments — the current case's live (non-deleted) rows plus
  // upload/delete inflight slots. Loaded alongside the case; reset on clear.
  attachments: [],
  attachmentUploadInflight: false,
  attachmentDeleteInflightId: null,
  annotationSaveInflightId: null,
  // PadRekko persistent map measurements (distance/area), case-scoped.
  mapAnnotations: [],
  mapAnnotationSaveInflight: false,
  mapAnnotationDeleteInflightId: null,
  // Operator completion status (per finding) inflight slot.
  findingCompletionInflightId: null,
  findingVerificationInflightId: null,
  casesList: null,
  casesListLoading: false,
  casesListError: null,
  // Compliance obligations (Day 6) — derived cross-case report + edit inflight.
  obligations: null,
  obligationsLoading: false,
  obligationsError: null,
  nextObsSaveInflight: false,
};

export const useQcStore = create((set, get) => ({
  /* ----------------------------- case state ------------------------------ */
  // Day 1 thin wrapper. Populated by loadCase alongside session/pads.
  // Components that read session-shaped state are unchanged; only the
  // inspector header banner + stage transition button read these.
  caseInfo: null,         // { id, name, session_id, current_stage, config,
                          //   stage_history, owner_org_id, participants[] }
  resolveGeometryInflightPadId: null,  // pad whose geometry resolve is in flight
  caseRole: null,         // 'dh_analyst' | 'customer_analyst' | 'operator_analyst'
  caseWriteable: false,   // boolean — caller can mutate at current_stage

  // Inflight for the PATCH /stage call. Drives the "Handing off…" spinner.
  advanceStageInflight: false,

  // Inflight for POST /cases/:id/participants. Drives the participants modal
  // submit spinner. Modal-local concern; we expose it on the store so the
  // modal can stay a pure consumer of store actions.
  addParticipantInflight: false,

  // Phase A — reverse stage transition + dispute clearing inflight slots.
  reverseStageInflight: false,
  clearDisputeInflightFindingId: null,
  validateInflightKey: null,
  kickbackInflightFindingId: null,
  resubmitInflightFindingId: null,
  // Phase B — reopen (complete → prior stage) inflight slot.
  reopenInflight: false,
  // Phase 2 — customer-as-closer inflight slots (close + send-back-to-operator).
  closeInflight: false,
  sendBackToOperatorInflight: false,
  // REM-576 — the handoff acts: release (producer), share and revoke (owner).
  releaseInflight: false,
  shareInflight: false,
  revokeShareInflight: false,

  /* ----------------------------- session state --------------------------- */
  session: null,
  // Shape after createSessionFromKmz / loadSession:
  // {
  //   id, name, source, pad_count, status, started_at, completed_at,
  //   pads: [
  //     { id, pad_name, ordinal, state_code, state_framework, status,
  //       aoi_geom, aoi_bbox, summary, image_snapshot, finding_count,
  //       completed_at }
  //   ]
  // }

  // Current inspector position — store-resident so tab switches preserve
  // cursor (the inspector unmounts but this survives). Seeded from
  // firstActionablePadIdx on load. Page refresh resets to first actionable
  // by re-running loadSession, which is the expected behavior.
  currentPadIdx: null,

  loading: false,
  error: null,

  /* ----------------------- per-pad draw + analyze state ------------------ */

  // True only while QcPolygonDrawer has a polygon in-progress. QcFindingsLayer
  // hides itself when true so the canvas snapshot doesn't bake prior polygons
  // into the image AI sees.
  drawingPolygon: false,

  // Pad currently mid-analyze (single slot — operator works one pad at a
  // time in v0). Used by the drawer to disable a second draw and by the
  // pad header to show a spinner.
  analyzeInflightPadId: null,

  // Pads currently mid-skip and mid-finish. Same per-pad-string slot
  // shape as analyzeInflightPadId so the inspector can show the spinner
  // on the correct button if the operator navigates mid-flight. Both
  // actions guard against each other AND against analyze on the same
  // pad — three operations, one-at-a-time per pad.
  skipInflightPadId:   null,
  finishInflightPadId: null,

  // Boolean (not per-pad) — export is session-scoped, not pad-scoped.
  // Drives the Download JSON button's Loader2 + "Exporting…" label
  // in QcSummary.
  exportInflight: false,

  // Set<findingKey> of findings currently being reanalyzed. A pad can have
  // multiple findings reanalyzing in parallel (rare but possible) without
  // trampling each other's spinner state.
  reanalyzeInflight: new Set(),

  // Set<findingKey> of findings the operator has marked addressed in this
  // session. Client-only — server doesn't track addressed state in v0.
  // Cleared on session change.
  dismissedFindingKeys: new Set(),

  // Set<findingKey> of findings the operator has hidden from the map for
  // this session. Card stays visible in the panel (so they can untoggle);
  // only the polygon layer filters on this. Client-only, cleared on
  // session change. Always toggleable regardless of caseWriteable.
  hiddenFindingKeys: new Set(),

  // {key, nonce} the findings layer subscribes to. dispatch zoomToFinding
  // bumps the nonce so repeated clicks on the same card re-trigger fit.
  zoomToPolygon: null,

  // findingKey of the card currently in correct-mode, or null. At-most-one
  // semantics — opening one card's correction box auto-closes the previous.
  // Textarea contents stay in the card's local state; this store flag is
  // only "which card is open."
  openCorrectFindingKey: null,

  /* ----------------------- polygon edit (Day 8 Phase 2) ------------------ */
  // DH service-provider polygon-edit mode. Set when the DH clicks the
  // "Edit polygon" toggle inside correct mode. Carries the originating
  // finding id (UUID) + the original polygon GeoJSON for restore-on-cancel.
  // QcFindingsLayer subscribes to dim sibling findings; QcPolygonDrawer
  // subscribes to swap entry button for floating Submit/Cancel toolbar.
  polygonEditMode: null,    // { findingId, findingIdx, padIdx, originalPolygon } | null
  submitPolygonInflight: false,

  /* ----------------------- notes (Day 8 Phase A) ------------------------- */
  // Polymorphic notes state, keyed by caseId. Each entry holds:
  //   { case: Note[], findings: Map<findingId, Note[]> }
  // Backend returns notes joined with author display info; we render
  // ${note.author_display ?? "Former member"}.
  // SSE case_note_added events flow through appendNoteFromSSE which
  // dedups by note.id (author optimistic + SSE echo for same note id).
  notesByCase: new Map(),

  // Inflight slots for compose UX. Granular so a finding-note submit
  // doesn't block a case-note submit elsewhere.
  caseNoteInflight: false,
  findingNoteInflightFindingId: null,
  editNoteInflightId: null,
  deleteNoteInflightId: null,
  // PadRekko attachments — the current case's live (non-deleted) rows plus
  // upload/delete inflight slots. Loaded alongside the case; reset on clear.
  attachments: [],
  attachmentUploadInflight: false,
  attachmentDeleteInflightId: null,
  annotationSaveInflightId: null,
  // PadRekko persistent map measurements (distance/area), case-scoped.
  mapAnnotations: [],
  mapAnnotationSaveInflight: false,
  mapAnnotationDeleteInflightId: null,
  // Operator completion status (per finding) inflight slot.
  findingCompletionInflightId: null,

  /* ----------------------- cases list (picker) lifecycle ------------------ */
  // Kept distinct from session/case/loading/error so reloading the picker
  // list (e.g. after a stage advance) doesn't blow away the currently-
  // loaded case + session.
  casesList: null,        // [{ id, name, current_stage, owner_org_id,
                          //   pad_count, my_role, updated_at }]
  casesListLoading: false,
  casesListError: null,
  // Compliance obligations (Day 6) — derived cross-case report + edit inflight.
  obligations: null,
  obligationsLoading: false,
  obligationsError: null,
  nextObsSaveInflight: false,
  // Project-landing dashboard — derived header + stat rollups + operator roster.
  projectDashboard: null,
  projectDashboardLoading: false,
  projectDashboardError: null,

  /* ----------------------------- actions ---------------------------------- */

  /**
   * Create a new case. POST /cases orchestrates case + session + pads +
   * participants in one server transaction. On success the response shape
   * matches what GET /cases/:id returns, so we drive the local state
   * directly from the create response — no extra GET roundtrip.
   *
   * Body shape: { name, kmzB64, config?: {autodetect_enabled: bool},
   *               participants: [{org_id, role}, {org_id, role}] }
   *
   * Returns the new case_id on success (so the caller can navigate / log),
   * undefined on failure (with `error` populated).
   */
  async createCase({ name, kmzB64, config, participants, sameTenant = false }) {
    if (get().loading) return undefined;
    set({ loading: true, error: null });

    try {
      const res = await fetchWithAuth(`${API}/cases`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // Migration 111. Explicit and boolean — the server will not infer it
          // from a one-entry participants array.
          same_tenant: sameTenant,
          name,
          kmz_b64: kmzB64,
          config: config ?? undefined,
          participants,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();

      // Server returns { case, session, pads, participants }. The owner
      // is dh_analyst at this point and the case starts in dh_review, so
      // we know caller role + writeable without re-querying.
      const pads = data.pads ?? [];
      set({
        caseInfo: { ...data.case, participants: data.participants ?? [], participant_users: data.participant_users ?? null },
        caseRole: "dh_analyst",
        caseWriteable: true,
        session: {
            ...data.session,
            pads,
            // Server-computed review coverage (reviewCounts.js). Carried on
            // session because that is where the pads live; nothing in the
            // browser recomputes or adjusts these.
            review_counts: data.review_counts ?? null,
            review_counts_stage: data.review_counts_stage ?? null,
          },
        currentPadIdx: firstActionablePadIdx(pads),
        loading: false,
      });
      return data.case.id;
    } catch (err) {
      console.error("[useQcStore] createCase", err);
      set({
        error: err?.message ?? String(err),
        loading: false,
      });
      return undefined;
    }
  },

  /**
   * Create a case from map-selected + buffered pads (the "Create New Case"
   * builder → NewCaseForm in from-selection mode). Full parity with createCase:
   * POSTs to /cases/from-selection, which writes session(source='map_selection')
   * +pads+case+3 participants+operating AOI+fan-out in one transaction. Uses
   * fetchWithAuth so the x-aiprime-project header rides along and project_id is
   * stamped.
   *
   * Body shape mirrors the KMZ door: { name, config?, participants:[{role,
   * org_id, user_id}], pads:[{pad_name, state_code?, geometry}] }.
   *
   * Does NOT populate caseInfo/session here — NewCaseForm routes to
   * /map/padrekko?caseId=<id> on success, and ReclamationQcTab loads it.
   *
   * @returns {Promise<object>} the created { case, session, pads, participants }.
   * @throws on validation/commit failure (message carries the offending pad).
   */
  async createCaseFromSelection({ name, config, participants, pads, same_tenant = false }) {
    set({ loading: true, error: null });
    try {
      const res = await fetchWithAuth(`${API}/cases/from-selection`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          same_tenant,
          name,
          config: config ?? undefined,
          participants,
          pads: (pads ?? []).map((p) => ({
            pad_name: p.pad_name,
            state_code: p.state_code,
            geometry: p.geometry,
          })),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
        const msg = body.error
          ? body.pad ? `${body.error} (pad: ${body.pad})` : body.error
          : `HTTP ${res.status}`;
        throw new Error(msg);
      }
      const data = await res.json();
      set({ loading: false });
      return data;
    } catch (err) {
      console.error("[useQcStore] createCaseFromSelection", err);
      set({ error: err?.message ?? String(err), loading: false });
      throw err;
    }
  },

  /**
   * Load a case by id. Fetches case + session + pads + participants in
   * one GET and populates the store for both the inspector (session-shaped
   * consumers) and the case header banner.
   *
   * @param {string} caseId
   * @param {Object} [opts]
   * @param {boolean} [opts.preserveCursor=false]
   *        When true, the inspector cursor (currentPadIdx) is NOT reset
   *        to firstActionablePadIdx. Used by event-driven refreshes
   *        (SSE pad_detection_completed) so the inspector doesn't auto-
   *        advance when the user only wanted the pad they're viewing to
   *        update. User-initiated loads (case open, picker click,
   *        retry) use the default = false so the cursor lands on the
   *        first actionable pad.
   */
  async loadCase(caseId, { preserveCursor = false } = {}) {
    if (!caseId) return;
    set({ loading: true, error: null });

    try {
      const res = await fetchWithAuth(
        `${API}/cases/${encodeURIComponent(caseId)}`
      );
      if (!res.ok) {
        const body = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const pads = data.pads ?? [];
      set({
        caseInfo: { ...data.case, participants: data.participants ?? [], participant_users: data.participant_users ?? null },
        caseRole: data.my_role ?? null,
        caseWriteable: data.writeable === true,
        session: {
            ...data.session,
            pads,
            // Server-computed review coverage (reviewCounts.js). Carried on
            // session because that is where the pads live; nothing in the
            // browser recomputes or adjusts these.
            review_counts: data.review_counts ?? null,
            review_counts_stage: data.review_counts_stage ?? null,
          },
        currentPadIdx: preserveCursor
          ? (get().currentPadIdx ?? firstActionablePadIdx(pads))
          : firstActionablePadIdx(pads),
        loading: false,
      });
      // Day 8 Phase A — hydrate notes alongside the case. Fire-and-forget;
      // errors are non-fatal and won't block case load.
      get().loadCaseNotes(caseId).catch((err) =>
        console.warn("[useQcStore] loadCaseNotes failed (non-fatal)", err)
      );
      // Attachments — hydrate alongside the case (same fire-and-forget contract).
      get().loadAttachments(caseId).catch((err) =>
        console.warn("[useQcStore] loadAttachments failed (non-fatal)", err)
      );
      // Persistent map measurements — hydrate alongside the case.
      get().loadMapAnnotations(caseId).catch((err) =>
        console.warn("[useQcStore] loadMapAnnotations failed (non-fatal)", err)
      );
    } catch (err) {
      console.error("[useQcStore] loadCase", err);
      set({
        error: err?.message ?? String(err),
        loading: false,
      });
    }
  },

  /**
   * Cases visible to the current org — owned + participating, newest first
   * by updated_at. Powers the picker case list.
   */
  async listCases({ stage, limit = 20, project } = {}) {
    set({ casesListLoading: true, casesListError: null });

    try {
      const params = new URLSearchParams();
      if (stage) params.set("stage", stage);
      if (limit) params.set("limit", String(limit));
      // Project filter — a concrete id narrows the list; "global"/absent = all.
      if (project && project !== "global") params.set("project", project);
      const qs = params.toString();
      const res = await fetchWithAuth(
        `${API}/cases${qs ? `?${qs}` : ""}`
      );
      if (!res.ok) {
        const body = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      set({ casesList: data.cases ?? [], casesListLoading: false });
    } catch (err) {
      console.error("[useQcStore] listCases", err);
      set({
        casesListError: err?.message ?? String(err),
        casesListLoading: false,
      });
    }
  },

  /**
   * Advance the loaded case to the next stage. Server validates the
   * transition under FOR UPDATE lock; on success it returns the updated
   * case + a transition record + an optional warning ("majority_skipped").
   *
   * Returns { ok: bool, warning?: {code, detail}, conflict?: {...} }.
   * On conflict (409 — another analyst advanced first), the caller should
   * refresh via loadCase to get the new stage.
   */
  async advanceStage({ toStage, notes, acknowledgeHandoff = false }) {
    const state = get();
    const caseId = state.caseInfo?.id;
    if (!caseId) return { ok: false };
    if (state.advanceStageInflight) return { ok: false };

    set({ advanceStageInflight: true, error: null });

    try {
      const res = await fetchWithAuth(
        `${API}/cases/${encodeURIComponent(caseId)}/stage`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to_stage: toStage,
            notes: notes ?? null,
            // Only ever true when the caller has seen the warnings and said
            // so. Absent by default, so a caller that forgets is stopped
            // rather than forwarding silently.
            ...(acknowledgeHandoff ? { acknowledge_handoff: true } : {}),
          }),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });

      if (res.status === 409) {
        set({ advanceStageInflight: false });
        // Two different 409s share this route and they need opposite
        // responses: a lost race means reload, a handoff warning means ask and
        // retry. Distinguished by `code` rather than guessed from the shape —
        // mishandled, the caller either loses the warning or reloads the case
        // for no reason.
        if (data?.code === "handoff_warnings") {
          return { ok: false, handoffWarnings: data };
        }
        // Concurrent advance won. Surface the conflict so the caller can
        // refresh via loadCase + show a "case advanced under you" toast.
        return { ok: false, conflict: data };
      }
      if (!res.ok) {
        // The banner shows this itself (REM-576), so the server's words come
        // back to the caller as well as into the store's `error`.
        const failure = actionFailure(res, data, "Hand-off");
        set({ error: failure.error, advanceStageInflight: false });
        return failure;
      }

      // Locally update caseInfo + re-derive caseWriteable from the new
      // stage. Avoids an extra GET; loadCase is still safe to call if the
      // caller wants the full server-canonical state.
      const updatedCase = data.case;
      const newStage = updatedCase.current_stage;
      const writerRole = writerRoleFor(newStage);
      const writeable = writerRole !== null && writerRole === state.caseRole;

      set({
        caseInfo: {
          ...state.caseInfo,
          ...updatedCase,
          // Preserve participants array (not returned by PATCH).
          participants: state.caseInfo?.participants ?? [],
        },
        caseWriteable: writeable,
        advanceStageInflight: false,
      });

      return {
        ok: true,
        warning: data.warning ?? null,
        transition: data.transition ?? null,
      };
    } catch (err) {
      console.error("[useQcStore] advanceStage", err);
      const failure = actionThrew(err, "Hand-off");
      set({
        error: failure.error,
        advanceStageInflight: false,
      });
      return failure;
    }
  },

  /**
   * Phase A reverse stage — moves the case one stage back, records the
   * reason in stage_history with direction='reverse', and flags any
   * disputed_finding_ids. Same fan-out shape as advanceStage but with
   * eventSubtype='stage_reverse_transition'.
   *
   * Returns { ok: bool, error?, status? }. Modal-local errors do NOT
   * write to the store's `error` slot — SendBackModal surfaces them
   * inline.
   */
  async reverseStage({ toStage, reason, disputedFindingIds = [] }) {
    const state = get();
    const caseId = state.caseInfo?.id;
    if (!caseId) return { ok: false, error: "No case loaded" };
    if (state.reverseStageInflight) return { ok: false, error: "Reverse in flight" };

    set({ reverseStageInflight: true });

    try {
      const res = await fetchWithAuth(
        `${API}/cases/${encodeURIComponent(caseId)}/reverse-stage`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to_stage: toStage,
            reason,
            disputed_finding_ids: disputedFindingIds,
          }),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });

      if (!res.ok) {
        set({ reverseStageInflight: false });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }

      // loadCase to refresh stage_history + findings (dispute flags) +
      // any local state derivations. Preserve cursor since this is a
      // user-initiated action that shouldn't auto-advance.
      await get().loadCase(caseId, { preserveCursor: true });
      set({ reverseStageInflight: false });
      return { ok: true, transition: data.transition ?? null };
    } catch (err) {
      console.error("[useQcStore] reverseStage", err);
      set({ reverseStageInflight: false });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /**
   * Phase B reopen — moves a 'complete' case back to a caller-chosen
   * prior stage. Authorization is service-provider-only (enforced by
   * the backend); the UI gates the button on isServiceProvider so the
   * affordance never shows for non-DH callers.
   *
   * Reuses the stage_reverse_transition SSE subtype, so the rail + toast
   * wiring already handles the fan-out — no extra collab-store changes.
   *
   * Returns { ok: bool, error?, status? }. Modal-local errors do NOT
   * write to the store's `error` slot — ReopenModal surfaces them inline.
   */
  async reopen({ toStage, reason }) {
    const state = get();
    const caseId = state.caseInfo?.id;
    if (!caseId) return { ok: false, error: "No case loaded" };
    if (state.reopenInflight) return { ok: false, error: "Reopen in flight" };

    set({ reopenInflight: true });

    try {
      const res = await fetchWithAuth(
        `${API}/cases/${encodeURIComponent(caseId)}/reopen`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to_stage: toStage,
            reason,
          }),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });

      if (!res.ok) {
        set({ reopenInflight: false });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }

      await get().loadCase(caseId, { preserveCursor: true });
      set({ reopenInflight: false });
      return { ok: true, transition: data.transition ?? null };
    } catch (err) {
      console.error("[useQcStore] reopen", err);
      set({ reopenInflight: false });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /**
   * Phase 2 — customer-close. POST /cases/:id/close (customer_analyst at
   * customer_review with operator_pass_count > 0). Optional reason. Refreshes
   * the case so the banner flips to the read-only 'complete' state. Modal
   * surfaces errors inline; does not write the store's `error` slot.
   */
  async closeCase({ reason, acknowledgeHandoff = false } = {}) {
    const state = get();
    const caseId = state.caseInfo?.id;
    if (!caseId) return { ok: false, error: "No case loaded" };
    if (state.closeInflight) return { ok: false, error: "Close in flight" };

    set({ closeInflight: true });

    try {
      const res = await fetchWithAuth(
        `${API}/cases/${encodeURIComponent(caseId)}/close`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...(reason && reason.trim() ? { reason: reason.trim() } : {}),
            ...(acknowledgeHandoff ? { acknowledge_handoff: true } : {}),
          }),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });

      // Same gate as the forward path, same handling. Checked before the
      // generic !res.ok branch so it is not flattened into a plain error.
      if (res.status === 409 && data?.code === "handoff_warnings") {
        set({ closeInflight: false });
        return { ok: false, handoffWarnings: data };
      }
      if (!res.ok) {
        set({ closeInflight: false });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }

      await get().loadCase(caseId, { preserveCursor: true });
      set({ closeInflight: false });
      return { ok: true, transition: data.transition ?? null };
    } catch (err) {
      console.error("[useQcStore] closeCase", err);
      set({ closeInflight: false });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /**
   * Phase 2 — customer send-back to operator. POST /cases/:id/send-back
   * (customer_analyst at customer_review with operator_pass_count > 0). Reason
   * REQUIRED. Unlike the forward handoff there is no all-pads-terminal gate,
   * and unlike the operator handback it does NOT increment operator_pass_count.
   * Modal surfaces errors inline.
   */
  async sendBackToOperator({ reason }) {
    const state = get();
    const caseId = state.caseInfo?.id;
    if (!caseId) return { ok: false, error: "No case loaded" };
    if (state.sendBackToOperatorInflight) return { ok: false, error: "Send-back in flight" };
    const trimmed = typeof reason === "string" ? reason.trim() : "";
    if (!trimmed) return { ok: false, error: "Reason is required" };

    set({ sendBackToOperatorInflight: true });

    try {
      const res = await fetchWithAuth(
        `${API}/cases/${encodeURIComponent(caseId)}/send-back`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason: trimmed }),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });

      if (!res.ok) {
        set({ sendBackToOperatorInflight: false });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }

      await get().loadCase(caseId, { preserveCursor: true });
      set({ sendBackToOperatorInflight: false });
      return { ok: true, transition: data.transition ?? null };
    } catch (err) {
      console.error("[useQcStore] sendBackToOperator", err);
      set({ sendBackToOperatorInflight: false });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /**
   * REM-576 — the producer releases the case to the owner. POST
   * /cases/:id/release (dh_analyst; any stage but complete). Irreversible: the
   * owner can open the case from now on.
   *
   * Returns { ok, status, error, detail, alreadyReleased }. alreadyReleased is
   * the server's own "already released" 409 (it carries released_at), which a
   * retried "Release and hand off" treats as done rather than as a failure.
   */
  async releaseCase() {
    const state = get();
    const caseId = state.caseInfo?.id;
    if (!caseId) return { ok: false, status: 0, error: "No case loaded", detail: null };
    if (state.releaseInflight) return { ok: false, status: 0, error: "Release in flight", detail: null };

    set({ releaseInflight: true });
    try {
      const res = await fetchWithAuth(
        `${API}/cases/${encodeURIComponent(caseId)}/release`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        set({ releaseInflight: false });
        return { ...actionFailure(res, data, "Release"), alreadyReleased: res.status === 409 && !!data?.released_at };
      }
      await get().loadCase(caseId, { preserveCursor: true });
      set({ releaseInflight: false });
      return { ok: true, status: res.status, error: null, detail: null, release: data.release ?? null };
    } catch (err) {
      console.error("[useQcStore] releaseCase", err);
      set({ releaseInflight: false });
      return actionThrew(err, "Release");
    }
  },

  /**
   * REM-576 — the owner shares the case with the respondent (the operator's
   * org). POST /cases/:id/share (customer_analyst).
   *
   * THE DEADLINE IS ALWAYS NAMED. A first share could take the org's default,
   * but that default is not shown anywhere, and after a revoke the server
   * refuses a share without one (decision 4). So the owner chooses, every
   * time, and nothing is sent until they have.
   *
   * Returns { ok, status, error, detail, share?, deadline_source? }.
   */
  async shareCase({ deadlineBasis } = {}) {
    const state = get();
    const caseId = state.caseInfo?.id;
    if (!caseId) return { ok: false, status: 0, error: "No case loaded", detail: null };
    if (!isResponseDeadlineBasis(deadlineBasis)) {
      return { ok: false, status: 0, error: "Choose a response deadline: 14, 30, 60 or 90 days", detail: null };
    }
    if (state.shareInflight) return { ok: false, status: 0, error: "Share in flight", detail: null };

    set({ shareInflight: true });
    try {
      const res = await fetchWithAuth(
        `${API}/cases/${encodeURIComponent(caseId)}/share`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ deadline_basis: deadlineBasis }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        set({ shareInflight: false });
        return actionFailure(res, data, "Share");
      }
      await get().loadCase(caseId, { preserveCursor: true });
      set({ shareInflight: false });
      return {
        ok: true, status: res.status, error: null, detail: null,
        share: data.share ?? null, deadline_source: data.deadline_source ?? null,
      };
    } catch (err) {
      console.error("[useQcStore] shareCase", err);
      set({ shareInflight: false });
      return actionThrew(err, "Share");
    }
  },

  /**
   * REM-576 — the owner withdraws the share. POST /cases/:id/share/revoke
   * (customer_analyst). Reason REQUIRED. The respondent loses access at once;
   * at operator_review the server also moves the case back to customer_review.
   *
   * Returns { ok, status, error, detail, moved_back?, current_stage? }.
   */
  async revokeShare({ reason } = {}) {
    const state = get();
    const caseId = state.caseInfo?.id;
    if (!caseId) return { ok: false, status: 0, error: "No case loaded", detail: null };
    const trimmed = typeof reason === "string" ? reason.trim() : "";
    if (!trimmed) return { ok: false, status: 0, error: "A reason is required", detail: null };
    if (state.revokeShareInflight) return { ok: false, status: 0, error: "Revoke in flight", detail: null };

    set({ revokeShareInflight: true });
    try {
      const res = await fetchWithAuth(
        `${API}/cases/${encodeURIComponent(caseId)}/share/revoke`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason: trimmed }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        set({ revokeShareInflight: false });
        return actionFailure(res, data, "Revoke");
      }
      await get().loadCase(caseId, { preserveCursor: true });
      set({ revokeShareInflight: false });
      return {
        ok: true, status: res.status, error: null, detail: null,
        moved_back: data.moved_back === true, current_stage: data.current_stage ?? null,
      };
    } catch (err) {
      console.error("[useQcStore] revokeShare", err);
      set({ revokeShareInflight: false });
      return actionThrew(err, "Revoke");
    }
  },

  /**
   * Resolve an open dispute. POST /findings/:id/resolve-dispute (100).
   *
   * THREE OUTCOMES, TWO PARTIES, AND THE SERVER DECIDES WHICH YOU MAY SEND:
   *
   *   upheld | overruled   the RECEIVER rules. Must be the current stage's
   *                        writer, and must not be the org that raised it.
   *   withdrawn            the RAISER takes it back. Only they may.
   *
   * Refusals come back 409 with a `detail` naming the party whose act it is.
   * The caller surfaces that text rather than a generic failure, because
   * "you are not the party this belongs to" is information, not an error.
   *
   * ── THE OPTIMISTIC UPDATE NO LONGER NULLS THE RAISE ─────────────────────
   *
   * It used to mirror the old server behaviour and wipe disputed_at,
   * disputed_by_* and disputed_note locally. Since 100 the objection SURVIVES
   * its ruling on the server, so a client that erased it would render a
   * resolved dispute with no record of what it had been about — and only until
   * the next loadCase, which is the worst kind of disagreement.
   */
  async resolveDispute(findingId, { resolution, note } = {}) {
    if (!findingId) return { ok: false, error: "No finding id" };
    if (get().clearDisputeInflightFindingId === findingId) {
      return { ok: false, error: "Resolution in flight" };
    }

    set({ clearDisputeInflightFindingId: findingId });

    try {
      const res = await fetchWithAuth(
        `${API}/findings/${encodeURIComponent(findingId)}/resolve-dispute`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ resolution, note }),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });

      if (!res.ok) {
        set({ clearDisputeInflightFindingId: null });
        return {
          ok: false,
          status: res.status,
          // `detail` first: on a 409 it names which party the act belongs to,
          // which is the only useful half of the response.
          error: data.detail || data.error || `HTTP ${res.status}`,
        };
      }

      // Optimistic local update: flip is_disputed and graft on the resolution
      // the server just recorded. Avoids a full loadCase round-trip for what is
      // structurally a one-row mutation.
      const r = data.dispute_resolution || {};
      set((s) => {
        if (!s.session?.pads) return { clearDisputeInflightFindingId: null };
        const pads = s.session.pads.map((p) => ({
          ...p,
          findings: (p.findings || []).map((f) =>
            f.id === findingId
              ? {
                  ...f,
                  is_disputed: false,
                  dispute_resolution: r.resolution ?? resolution,
                  dispute_resolution_note: r.note ?? note,
                  dispute_resolution_by_user_id: r.by_user_id ?? null,
                  dispute_resolution_by_org_id: r.by_org_id ?? null,
                  dispute_resolution_at: r.at ?? new Date().toISOString(),
                }
              : f
          ),
        }));
        return {
          session: { ...s.session, pads },
          clearDisputeInflightFindingId: null,
        };
      });
      return { ok: true };
    } catch (err) {
      console.error("[useQcStore] resolveDispute", err);
      set({ clearDisputeInflightFindingId: null });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  // Persisted soft-delete of a flat-wrong finding, with a mandatory rationale.
  // Distinct from the client-only dismissFinding (panel declutter) — this is
  // server-authoritative and the backend excludes the finding from every active
  // surface. On success we drop it locally so the panel + map update without a
  // full loadCase round-trip (mirrors clearDispute's optimistic path).
  async removeFinding(findingId, reason) {
    if (!findingId) return { ok: false, error: "No finding id" };
    const trimmed = typeof reason === "string" ? reason.trim() : "";
    if (!trimmed) return { ok: false, error: "A removal reason is required" };
    try {
      const res = await fetchWithAuth(
        `${API}/findings/${encodeURIComponent(findingId)}/remove`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason: trimmed }),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }
      set((s) => {
        if (!s.session?.pads) return {};
        const pads = s.session.pads.map((p) => ({
          ...p,
          findings: (p.findings || []).filter((f) => f.id !== findingId),
        }));
        return { session: { ...s.session, pads } };
      });
      return { ok: true };
    } catch (err) {
      console.error("[useQcStore] removeFinding", err);
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  // Phase 2 — customer (UL) kicks a finding back to DH for rework. Optimistic
  // in-place update on success (mirrors clearDispute): flip the finding's
  // kicked_back_to_dh on. The case stays at customer_review; the SSE reconciler
  // refreshes the OTHER party. note must be >= 10 chars (backend re-checks).
  async kickBackFinding(findingId, note) {
    if (!findingId) return { ok: false, error: "No finding id" };
    const trimmed = typeof note === "string" ? note.trim() : "";
    if (trimmed.length < 10) {
      return { ok: false, error: "A kickback note of at least 10 characters is required" };
    }
    if (get().kickbackInflightFindingId === findingId) {
      return { ok: false, error: "Kickback in flight" };
    }
    set({ kickbackInflightFindingId: findingId });
    try {
      const res = await fetchWithAuth(
        `${API}/findings/${encodeURIComponent(findingId)}/kickback`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ note: trimmed }),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        set({ kickbackInflightFindingId: null });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }
      set((s) => {
        if (!s.session?.pads) return { kickbackInflightFindingId: null };
        const pads = s.session.pads.map((p) => ({
          ...p,
          findings: (p.findings || []).map((f) =>
            f.id === findingId
              ? {
                  ...f,
                  kicked_back_to_dh: true,
                  kicked_back_to_dh_at: data.kicked_back_to_dh_at ?? new Date().toISOString(),
                  kicked_back_to_dh_note: trimmed,
                  dh_resubmitted_after_kickback: false,
                }
              : f
          ),
        }));
        return { session: { ...s.session, pads }, kickbackInflightFindingId: null };
      });
      return { ok: true };
    } catch (err) {
      console.error("[useQcStore] kickBackFinding", err);
      set({ kickbackInflightFindingId: null });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  // Phase 2 — DH resubmits a kicked-back finding to the customer. Clears the
  // active kickback state in place + bumps the local dh_pass_count to match the
  // server. note is optional. Mirrors kickBackFinding's optimistic path.
  async resubmitFinding(findingId, note) {
    if (!findingId) return { ok: false, error: "No finding id" };
    if (get().resubmitInflightFindingId === findingId) {
      return { ok: false, error: "Resubmit in flight" };
    }
    const trimmed = typeof note === "string" ? note.trim() : "";
    set({ resubmitInflightFindingId: findingId });
    try {
      const res = await fetchWithAuth(
        `${API}/findings/${encodeURIComponent(findingId)}/resubmit`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(trimmed ? { note: trimmed } : {}),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        set({ resubmitInflightFindingId: null });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }
      set((s) => {
        if (!s.session?.pads) return { resubmitInflightFindingId: null };
        const pads = s.session.pads.map((p) => ({
          ...p,
          findings: (p.findings || []).map((f) =>
            f.id === findingId
              ? {
                  ...f,
                  kicked_back_to_dh: false,
                  kicked_back_to_dh_at: null,
                  kicked_back_to_dh_by_user_id: null,
                  kicked_back_to_dh_by_org_id: null,
                  kicked_back_to_dh_note: null,
                  dh_resubmitted_after_kickback: true,
                }
              : f
          ),
        }));
        const caseInfo = s.caseInfo
          ? {
              ...s.caseInfo,
              dh_pass_count: data.dh_pass_count ?? ((s.caseInfo.dh_pass_count ?? 0) + 1),
            }
          : s.caseInfo;
        return { session: { ...s.session, pads }, caseInfo, resubmitInflightFindingId: null };
      });
      return { ok: true, dh_pass_count: data.dh_pass_count };
    } catch (err) {
      console.error("[useQcStore] resubmitFinding", err);
      set({ resubmitInflightFindingId: null });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /* ------------------------- notes (Day 8 Phase A) ----------------------- */

  /**
   * Fetch all active notes for a case, splitting case-level + per-finding.
   * Server returns chronological ascending per Flag C. We rebuild the per-
   * caseId entry in notesByCase from scratch.
   */
  async loadCaseNotes(caseId) {
    if (!caseId) return { ok: false };
    try {
      const res = await fetchWithAuth(
        `${API}/cases/${encodeURIComponent(caseId)}/notes`
      );
      if (!res.ok) {
        const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
        return { ok: false, error: data.error || `HTTP ${res.status}` };
      }
      const { notes } = await res.json();
      const caseNotes = [];
      const findingNotes = new Map();
      for (const n of notes ?? []) {
        if (n.target_type === "case") {
          caseNotes.push(n);
        } else if (n.target_type === "finding") {
          const list = findingNotes.get(n.target_id) ?? [];
          list.push(n);
          findingNotes.set(n.target_id, list);
        }
      }
      set((s) => {
        const next = new Map(s.notesByCase);
        next.set(caseId, { case: caseNotes, findings: findingNotes });
        return { notesByCase: next };
      });
      return { ok: true };
    } catch (err) {
      console.error("[useQcStore] loadCaseNotes", err);
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /* ------------------------- attachments (Day 1) ------------------------- */

  /**
   * Fetch the case's live attachments (photos + documents) with presigned
   * URLs. Replaces the `attachments` array wholesale. Non-fatal on error.
   */
  async loadAttachments(caseId) {
    if (!caseId) return { ok: false };
    try {
      const res = await fetchWithAuth(
        `${API}/cases/${encodeURIComponent(caseId)}/attachments`
      );
      if (!res.ok) {
        const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
        return { ok: false, error: data.error || `HTTP ${res.status}` };
      }
      const { attachments } = await res.json();
      set({ attachments: attachments ?? [] });
      return { ok: true };
    } catch (err) {
      console.error("[useQcStore] loadAttachments", err);
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /**
   * Upload a photo or document to a pad (findingId null) or a finding.
   * Multipart — the body is FormData, so Content-Type is intentionally left
   * unset (the browser fills in the multipart boundary). The server validates
   * type/size/MIME and returns the row + presigned URLs, which we append.
   */
  async uploadAttachment({ padId, findingId = null, file, caption = null }) {
    const caseId = get().caseInfo?.id;
    if (!caseId || !padId || !file) return { ok: false, error: "Missing case, pad, or file" };
    if (get().attachmentUploadInflight) return { ok: false, error: "Upload already in flight" };

    set({ attachmentUploadInflight: true });
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (findingId) fd.append("finding_id", findingId);
      if (caption && caption.trim()) fd.append("caption", caption.trim());

      const res = await fetchWithAuth(
        `${API}/cases/${encodeURIComponent(caseId)}/pads/${encodeURIComponent(padId)}/attachments`,
        { method: "POST", body: fd }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        set({ attachmentUploadInflight: false });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }
      set((s) => ({
        attachments: [...s.attachments, data.attachment],
        attachmentUploadInflight: false,
      }));
      return { ok: true, attachment: data.attachment };
    } catch (err) {
      console.error("[useQcStore] uploadAttachment", err);
      set({ attachmentUploadInflight: false });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /**
   * Resolve which geometry this pad's vision analysis should be framed on.
   *
   * ENTRY POINT B. Autodetect is entry point A and calls the same server
   * function; this lets the analyst see what their AOI resolves to — and
   * whether it clips a real pad — before committing to a detection run.
   *
   * RESOLVING IS A WRITE. It persists the witness columns, so the server gates
   * it on the stage writer (requireCaseAccessByPad enforces writeability). The
   * CALLER MUST GATE THE CONTROL ON caseWriteable rather than relying on this:
   * showing a button that 403s is the trap caseStages.js:119-133 names by name,
   * where the gate allows, a sibling refuses, and the user is shown a control
   * that fails. The guard below is the second line, not the first.
   *
   * Merges the response into the pad row so the banner and the resolved-outline
   * layer update without a session reload. containment_frac rides along in
   * memory only — migration 123 does not persist it, because the arm CHECK
   * requires source_feature_id IS NULL on user_aoi — so the fraction in the
   * clipped banner is gone after a reload and the copy says less rather than
   * something wrong.
   */
  async resolvePadGeometry(padId, { dataset = null } = {}) {
    if (!padId) return { ok: false, error: "No pad id" };
    if (!get().caseWriteable) {
      // Not your turn at this stage. The server would refuse too; refusing here
      // keeps the user out of a round trip that ends in a 403 toast.
      return { ok: false, error: "Read-only at this stage" };
    }
    if (get().resolveGeometryInflightPadId) {
      return { ok: false, error: "Resolve already in flight" };
    }

    set({ resolveGeometryInflightPadId: padId });
    try {
      const res = await fetchWithAuth(
        `${API}/pads/${encodeURIComponent(padId)}/geometry/resolve`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(dataset ? { dataset } : {}),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        set({ resolveGeometryInflightPadId: null });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }
      set((s) => ({
        resolveGeometryInflightPadId: null,
        session: s.session ? {
          ...s.session,
          pads: s.session.pads.map((p) => (p.id === padId ? { ...p, ...data } : p)),
        } : s.session,
      }));
      return { ok: true, resolution: data };
    } catch (err) {
      console.error("[useQcStore] resolvePadGeometry", err);
      set({ resolveGeometryInflightPadId: null });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /**
   * Soft-delete an attachment. Server authorizes (uploader OR DH service
   * provider). On success the row drops from the local list.
   */
  async deleteAttachment(id) {
    if (!id) return { ok: false, error: "No attachment id" };
    if (get().attachmentDeleteInflightId) return { ok: false, error: "Delete already in flight" };

    set({ attachmentDeleteInflightId: id });
    try {
      const res = await fetchWithAuth(
        `${API}/attachments/${encodeURIComponent(id)}`,
        { method: "DELETE" }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        set({ attachmentDeleteInflightId: null });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }
      set((s) => ({
        attachments: s.attachments.filter((a) => a.id !== id),
        attachmentDeleteInflightId: null,
      }));
      return { ok: true };
    } catch (err) {
      console.error("[useQcStore] deleteAttachment", err);
      set({ attachmentDeleteInflightId: null });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /**
   * Replace a photo's annotation overlay (arrows/circles/text). Sends the full
   * array; the server validates + authorizes and returns the stored value,
   * which we merge back onto the attachment row.
   */
  async saveAnnotations(attachmentId, annotations) {
    if (!attachmentId) return { ok: false, error: "No attachment id" };
    set({ annotationSaveInflightId: attachmentId });
    try {
      const res = await fetchWithAuth(
        `${API}/attachments/${encodeURIComponent(attachmentId)}/annotations`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ annotations: annotations ?? [] }),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        set({ annotationSaveInflightId: null });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }
      set((s) => ({
        attachments: s.attachments.map((a) =>
          a.id === attachmentId ? { ...a, annotations: data.annotations } : a
        ),
        annotationSaveInflightId: null,
  // PadRekko persistent map measurements (distance/area), case-scoped.
  mapAnnotations: [],
  mapAnnotationSaveInflight: false,
  mapAnnotationDeleteInflightId: null,
  // Operator completion status (per finding) inflight slot.
  findingCompletionInflightId: null,
      }));
      return { ok: true, annotations: data.annotations };
    } catch (err) {
      console.error("[useQcStore] saveAnnotations", err);
      set({ annotationSaveInflightId: null });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /* ------------------- compliance obligations (Day 6) -------------------- */

  /** Load the derived obligations report (optional active-project filter). */
  async loadObligations({ project } = {}) {
    set({ obligationsLoading: true, obligationsError: null });
    try {
      const qs = project && project !== "global" ? `?project=${encodeURIComponent(project)}` : "";
      const res = await fetchWithAuth(`${API}/obligations${qs}`);
      if (!res.ok) {
        const body = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      set({ obligations: data, obligationsLoading: false });
      return { ok: true };
    } catch (err) {
      console.error("[useQcStore] loadObligations", err);
      set({ obligationsError: err?.message ?? String(err), obligationsLoading: false });
      return { ok: false };
    }
  },

  /* --------------------- project-landing dashboard ---------------------- */

  /**
   * Load the project dashboard (header + stat rollups + operator roster) for
   * the project-landing surface. Concrete project id only — "global"/absent
   * has no project to scope to (the component shows a pick-a-project prompt).
   * Fires the obligations load in parallel so the strip can reuse the same
   * existing /obligations summary.
   */
  async loadProjectDashboard(projectId) {
    if (!projectId || projectId === "global") {
      set({ projectDashboard: null, projectDashboardError: null, projectDashboardLoading: false });
      return { ok: false };
    }
    set({ projectDashboardLoading: true, projectDashboardError: null });
    get().loadObligations({ project: projectId }).catch((e) => { console.error("[ui/src/stores/useQcStore.js] loadObligations({ project: projectId }) failed (swallowed):", e); });
    try {
      const res = await fetchWithAuth(
        `${API}/projects/${encodeURIComponent(projectId)}/dashboard`
      );
      if (!res.ok) {
        const body = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      set({ projectDashboard: data, projectDashboardLoading: false });
      return { ok: true };
    } catch (err) {
      console.error("[useQcStore] loadProjectDashboard", err);
      set({ projectDashboardError: err?.message ?? String(err), projectDashboardLoading: false });
      return { ok: false };
    }
  },

  /** UL edits a case's recommended next-observation date (YYYY-MM-DD). */
  async saveNextObservation(caseId, date) {
    if (!caseId || !date) return { ok: false, error: "Missing case or date" };
    set({ nextObsSaveInflight: true });
    try {
      const res = await fetchWithAuth(
        `${API}/cases/${encodeURIComponent(caseId)}/next-observation`,
        { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ next_observation_date: date }) }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        set({ nextObsSaveInflight: false });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }
      set((s) => ({
        nextObsSaveInflight: false,
        caseInfo: s.caseInfo?.id === caseId
          ? { ...s.caseInfo, next_observation_date: data.next_observation_date, next_observation_basis: data.next_observation_basis }
          : s.caseInfo,
      }));
      return { ok: true };
    } catch (err) {
      console.error("[useQcStore] saveNextObservation", err);
      set({ nextObsSaveInflight: false });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /* ------------------- operator completion status (Day 4) ---------------- */

  /**
   * Set a finding's remediation completion status (operator only, during
   * operator_review — server enforces). completed/not_completed require note +
   * responsible_party + completion_date; pending nulls them. Refreshes the case
   * (preserveCursor) so the map repaints and finding state stays fresh.
   */
  async setFindingCompletion(findingId, { status, note, responsible_party, completion_date } = {}) {
    if (!findingId) return { ok: false, error: "No finding id" };
    if (get().findingCompletionInflightId) return { ok: false, error: "Update in flight" };
    set({ findingCompletionInflightId: findingId });
    try {
      const res = await fetchWithAuth(
        `${API}/findings/${encodeURIComponent(findingId)}/completion`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status, note, responsible_party, completion_date }),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        set({ findingCompletionInflightId: null });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }
      const caseId = get().caseInfo?.id;
      if (caseId) await get().loadCase(caseId, { preserveCursor: true });
      set({ findingCompletionInflightId: null });
      return { ok: true, completion: data.completion };
    } catch (err) {
      console.error("[useQcStore] setFindingCompletion", err);
      set({ findingCompletionInflightId: null });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  // THE OWNER'S VERDICT, beside setFindingCompletion and deliberately not part
  // of it. completion_status is the respondent's report of their own work;
  // verification_status is the owner's verdict on that report. Two endpoints,
  // two inflight flags, so a verdict in flight never disables the completion
  // control or vice versa — they are written by different orgs and can be in
  // flight at once.
  async setFindingVerification(findingId, { status, note } = {}) {
    if (!findingId) return { ok: false, error: "No finding id" };
    if (get().findingVerificationInflightId) return { ok: false, error: "Update in flight" };
    set({ findingVerificationInflightId: findingId });
    try {
      const res = await fetchWithAuth(
        `${API}/findings/${encodeURIComponent(findingId)}/verification`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status, note }),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        set({ findingVerificationInflightId: null });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }
      const caseId = get().caseInfo?.id;
      if (caseId) await get().loadCase(caseId, { preserveCursor: true });
      set({ findingVerificationInflightId: null });
      return { ok: true, verification: data.verification };
    } catch (err) {
      console.error("[useQcStore] setFindingVerification", err);
      set({ findingVerificationInflightId: null });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /* ------------------- persistent map measurements (Day 3) --------------- */

  async loadMapAnnotations(caseId) {
    if (!caseId) return { ok: false };
    try {
      const res = await fetchWithAuth(
        `${API}/cases/${encodeURIComponent(caseId)}/map-annotations`
      );
      if (!res.ok) {
        const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
        return { ok: false, error: data.error || `HTTP ${res.status}` };
      }
      const { annotations } = await res.json();
      set({ mapAnnotations: annotations ?? [] });
      return { ok: true };
    } catch (err) {
      console.error("[useQcStore] loadMapAnnotations", err);
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /**
   * Save a completed measurement. payload: { annotation_type, geometry,
   * measurement_value, measurement_unit, measurement_raw, pad_id?, finding_id?,
   * label? }. Appends the returned row.
   */
  async saveMapAnnotation(payload) {
    const caseId = get().caseInfo?.id;
    if (!caseId) return { ok: false, error: "No case loaded" };
    if (get().mapAnnotationSaveInflight) return { ok: false, error: "Save in flight" };
    set({ mapAnnotationSaveInflight: true });
    try {
      const res = await fetchWithAuth(
        `${API}/cases/${encodeURIComponent(caseId)}/map-annotations`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        set({ mapAnnotationSaveInflight: false });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }
      set((s) => ({
        mapAnnotations: [...s.mapAnnotations, data.annotation],
        mapAnnotationSaveInflight: false,
      }));
      return { ok: true, annotation: data.annotation };
    } catch (err) {
      console.error("[useQcStore] saveMapAnnotation", err);
      set({ mapAnnotationSaveInflight: false });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  async deleteMapAnnotation(id) {
    if (!id) return { ok: false, error: "No annotation id" };
    if (get().mapAnnotationDeleteInflightId) return { ok: false, error: "Delete in flight" };
    set({ mapAnnotationDeleteInflightId: id });
    try {
      const res = await fetchWithAuth(
        `${API}/map-annotations/${encodeURIComponent(id)}`,
        { method: "DELETE" }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        set({ mapAnnotationDeleteInflightId: null });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }
      set((s) => ({
        mapAnnotations: s.mapAnnotations.filter((a) => a.id !== id),
        mapAnnotationDeleteInflightId: null,
  // Operator completion status (per finding) inflight slot.
  findingCompletionInflightId: null,
      }));
      return { ok: true };
    } catch (err) {
      console.error("[useQcStore] deleteMapAnnotation", err);
      set({ mapAnnotationDeleteInflightId: null });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /**
   * Append a server-returned note into the per-case entry. Dedups by
   * note.id — SSE echoes for the author's own optimistic insert and
   * concurrent participants both flow through here cleanly.
   */
  _insertNote(note) {
    if (!note?.case_id || !note?.id || !note?.target_type) return;
    set((s) => {
      const entry = s.notesByCase.get(note.case_id) ?? {
        case: [],
        findings: new Map(),
      };
      const nextCase = entry.case.slice();
      const nextFindings = new Map(entry.findings);

      if (note.target_type === "case") {
        if (nextCase.some((n) => n.id === note.id)) return {};
        nextCase.push(note);
      } else {
        const fid = note.target_id;
        const list = (nextFindings.get(fid) ?? []).slice();
        if (list.some((n) => n.id === note.id)) return {};
        list.push(note);
        nextFindings.set(fid, list);
      }

      const nextMap = new Map(s.notesByCase);
      nextMap.set(note.case_id, { case: nextCase, findings: nextFindings });
      return { notesByCase: nextMap };
    });
  },

  /**
   * SSE handler — called by useCollaborationStore when a case_note_added
   * event arrives. Body of the full note isn't in the event metadata
   * (preview only), so we re-fetch the singleton via GET to capture the
   * authoritative row + author_display join.
   *
   * Cheap dedup: if the case has no existing entry, GET also seeds it.
   */
  async appendNoteFromSSE({ case_id, note_id }) {
    if (!case_id || !note_id) return;
    const entry = get().notesByCase.get(case_id);
    if (entry) {
      const seen = entry.case.some((n) => n.id === note_id)
        || [...entry.findings.values()].some((list) => list.some((n) => n.id === note_id));
      if (seen) return;
    }
    // No singleton-by-id endpoint; refresh the case's notes — keeps state
    // simple and the round-trip cost is small (one query, partial index).
    await get().loadCaseNotes(case_id);
  },

  /**
   * POST a case-level note. Optimistic insert with a temp id; replaced
   * by the server's row on success. Fires case_note_added fanout (handled
   * server-side), so SSE will echo the same id — dedup in _insertNote
   * silently drops the echo.
   */
  async addCaseNote(caseId, body) {
    if (!caseId) return { ok: false, error: "No case id" };
    const trimmed = (body ?? "").trim();
    if (!trimmed) return { ok: false, error: "Body must not be empty" };
    if (get().caseNoteInflight) return { ok: false, error: "Submit in flight" };

    set({ caseNoteInflight: true });
    try {
      const res = await fetchWithAuth(
        `${API}/cases/${encodeURIComponent(caseId)}/notes`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ body: trimmed }),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        set({ caseNoteInflight: false });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }
      get()._insertNote(data.note);
      set({ caseNoteInflight: false });
      return { ok: true, note: data.note };
    } catch (err) {
      console.error("[useQcStore] addCaseNote", err);
      set({ caseNoteInflight: false });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /**
   * POST a finding-level note. Silent on the server (no fanout) per spec.
   */
  async addFindingNote(findingId, caseId, body) {
    if (!findingId || !caseId) return { ok: false, error: "Missing ids" };
    const trimmed = (body ?? "").trim();
    if (!trimmed) return { ok: false, error: "Body must not be empty" };
    if (get().findingNoteInflightFindingId === findingId) {
      return { ok: false, error: "Submit in flight" };
    }

    set({ findingNoteInflightFindingId: findingId });
    try {
      const res = await fetchWithAuth(
        `${API}/findings/${encodeURIComponent(findingId)}/notes`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ body: trimmed }),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        set({ findingNoteInflightFindingId: null });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }
      get()._insertNote(data.note);
      set({ findingNoteInflightFindingId: null });
      return { ok: true, note: data.note };
    } catch (err) {
      console.error("[useQcStore] addFindingNote", err);
      set({ findingNoteInflightFindingId: null });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /**
   * PATCH an existing note's body. Returns updated note row; replaces
   * in place by id. Server enforces author-or-service-provider auth.
   */
  async editNote(noteId, body) {
    if (!noteId) return { ok: false, error: "No note id" };
    const trimmed = (body ?? "").trim();
    if (!trimmed) return { ok: false, error: "Body must not be empty" };
    if (get().editNoteInflightId === noteId) return { ok: false, error: "Edit in flight" };

    set({ editNoteInflightId: noteId });
    try {
      const res = await fetchWithAuth(
        `${API}/notes/${encodeURIComponent(noteId)}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ body: trimmed }),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        set({ editNoteInflightId: null });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }
      const updated = data.note;
      set((s) => {
        const entry = s.notesByCase.get(updated.case_id);
        if (!entry) {
          set({ editNoteInflightId: null });
          return {};
        }
        const replaceInList = (list) => list.map((n) => n.id === updated.id ? updated : n);
        const next = new Map(s.notesByCase);
        next.set(updated.case_id, {
          case: replaceInList(entry.case),
          findings: new Map(
            [...entry.findings].map(([fid, list]) => [fid, replaceInList(list)])
          ),
        });
        return { notesByCase: next };
      });
      set({ editNoteInflightId: null });
      return { ok: true, note: updated };
    } catch (err) {
      console.error("[useQcStore] editNote", err);
      set({ editNoteInflightId: null });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /**
   * Soft-delete a note. Local state removes the row entirely — the
   * server still has it for audit.
   */
  async deleteNote(noteId, caseId) {
    if (!noteId || !caseId) return { ok: false, error: "Missing ids" };
    if (get().deleteNoteInflightId === noteId) return { ok: false, error: "Delete in flight" };

    set({ deleteNoteInflightId: noteId });
    try {
      const res = await fetchWithAuth(
        `${API}/notes/${encodeURIComponent(noteId)}`,
        { method: "DELETE" }
      );
      if (!res.ok && res.status !== 204) {
        const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
        set({ deleteNoteInflightId: null });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }
      set((s) => {
        const entry = s.notesByCase.get(caseId);
        if (!entry) return {};
        const filterOut = (list) => list.filter((n) => n.id !== noteId);
        const nextFindings = new Map(
          [...entry.findings].map(([fid, list]) => [fid, filterOut(list)])
        );
        const next = new Map(s.notesByCase);
        next.set(caseId, { case: filterOut(entry.case), findings: nextFindings });
        return { notesByCase: next };
      });
      set({ deleteNoteInflightId: null });
      return { ok: true };
    } catch (err) {
      console.error("[useQcStore] deleteNote", err);
      set({ deleteNoteInflightId: null });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /**
   * Add a participant org to the loaded case in a specific role. Owner-only
   * on the backend (caller must be dh_analyst). On success, refreshes the
   * full case via loadCase so the participants array and any other server
   * state stay canonical.
   *
   * Returns { ok: bool, error?, status? }. Errors do NOT write to the
   * store's `error` slot — participant-add errors are modal-local, not
   * global. The modal surfaces them inline.
   */
  async addParticipant({ orgId, role }) {
    const state = get();
    const caseId = state.caseInfo?.id;
    if (!caseId) return { ok: false, error: "No case loaded" };
    if (state.addParticipantInflight) return { ok: false, error: "Add in flight" };

    set({ addParticipantInflight: true });

    try {
      const res = await fetchWithAuth(
        `${API}/cases/${encodeURIComponent(caseId)}/participants`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ org_id: orgId, role }),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });

      if (!res.ok) {
        set({ addParticipantInflight: false });
        return {
          ok: false,
          status: res.status,
          error: data.error || `HTTP ${res.status}`,
        };
      }

      // Refresh canonical case state (participants array + everything).
      // loadCase clears its own inflight; we clear ours after.
      await get().loadCase(caseId);
      set({ addParticipantInflight: false });
      return { ok: true };
    } catch (err) {
      console.error("[useQcStore] addParticipant", err);
      set({ addParticipantInflight: false });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /**
   * Day 4 — Kick off lazy autodetect for a pad. No-op if autodetect
   * is not enabled on the case, or if the pad isn't 'none' state
   * (backend returns 409 in those cases — we just log + return).
   *
   * Returns { ok, status?, error? }. On success the backend has set
   * detection_status='pending' and the SSE listener will fire
   * pad_detection_completed/failed when work finishes.
   */
  async kickoffAutodetect(padId, { retry = false } = {}) {
    const state = get();
    const caseInfo = state.caseInfo;
    if (!caseInfo) return { ok: false, error: "No case loaded" };
    if (!caseInfo.config?.autodetect_enabled) {
      return { ok: false, error: "Autodetect not enabled on this case" };
    }

    // Day 4 — no dataset hint sent. User-visibility on the map is
    // decoupled from autodetect quality (server-side detection), and
    // PadRekkoPanel's tier-1 "visible+covering" preference can pick
    // older imagery when newer covering imagery exists. Backend
    // resolveDatasetForPad with no hint picks newest pad-covering
    // (fine LOD preferred) — the right semantic for reclamation QC.
    try {
      const res = await fetchWithAuth(
        `${API}/pads/${encodeURIComponent(padId)}/autodetect/start`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ retry }),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        console.warn("[useQcStore] kickoffAutodetect non-ok", res.status, data);
        return { ok: false, status: res.status, error: data.error };
      }
      // Refresh the case so the local pad row reflects the new status.
      await get().loadCase(caseInfo.id);
      return { ok: true };
    } catch (err) {
      console.error("[useQcStore] kickoffAutodetect threw", err);
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /**
   * Retry autodetect for a failed pad. Thin wrapper around
   * kickoffAutodetect with retry=true.
   */
  async retryAutodetect(padId) {
    return get().kickoffAutodetect(padId, { retry: true });
  },

  /**
   * Manual recovery for a stalled cascade — dispatch every pad still at
   * detection_status='none'. Fired from the inspector's "Analyze remaining"
   * action. Dispatches all undispatched pads (bounded by the small pad count;
   * user-initiated). Returns a summary so the caller can surface failures.
   */
  async analyzeRemaining() {
    const caseInfo = get().caseInfo;
    if (!caseInfo?.config?.autodetect_enabled) {
      return { ok: false, error: "Autodetect not enabled on this case" };
    }
    const pads = get().session?.pads ?? [];
    const nonePads = pads.filter((p) => (p.detection_status ?? "none") === "none");
    if (nonePads.length === 0) return { ok: true, dispatched: 0, failed: 0, total: 0 };

    let dispatched = 0;
    let failed = 0;
    for (const p of nonePads) {
      const res = await get().kickoffAutodetect(p.id);
      if (res?.ok) dispatched += 1;
      else failed += 1;
    }
    // Refresh so pad rows reflect the new pending/running states.
    await get().loadCase(caseInfo.id, { preserveCursor: true });
    return { ok: failed === 0, dispatched, failed, total: nonePads.length };
  },

  /**
   * Move the inspector cursor. Lives in the store so tab switches don't
   * reset it. In #8, Skip/Finish in the inspector will call this AFTER
   * updating server-side pad.status, in the same action.
   */
  setCurrentPadIdx(idx) {
    set({ currentPadIdx: idx });
  },

  /**
   * Dismiss the current error. v0 has a single error field shared across
   * session-load, analyze, and reanalyze failures; the inspector's error
   * banner calls this on dismiss. v1 will likely move to typed errors
   * with per-surface routing.
   */
  clearError() {
    set({ error: null });
  },

  /**
   * Drawer-state toggle. QcPolygonDrawer flips this true on draw start,
   * false on commit/cancel. QcFindingsLayer hides itself when true so the
   * canvas snapshot is clean.
   */
  setDrawingPolygon(value) {
    set({ drawingPolygon: !!value });
  },

  /**
   * Analyze a freshly-drawn polygon on the current pad.
   *
   * Server contract (POST /sessions/:id/pads/:pad_id/findings/analyze):
   *   body: { image_b64, polygons: [{idx, wkt}], media_type? }
   *   returns: { findings: [{idx, analysis}], usage, model }
   *
   * Response is lean (no polygon_geom, no history), so we reconstruct the
   * local finding shape from request + response + a synthetic created
   * history entry that mirrors what the server wrote. Authoritative state
   * restores on next loadSession.
   *
   * One inflight slot per session (analyzeInflightPadId). Returns
   * boolean: true on success, false on rejection (already inflight, no
   * session, etc.) or failure.
   */
  async analyzeFinding({ padIdx, polygonWkt, polygonGeoJson, imageB64, mediaType }) {
    const state = get();
    const session = state.session;
    if (!session) return false;
    if (state.analyzeInflightPadId) return false;

    const pad = session.pads?.[padIdx];
    if (!pad) return false;

    const idx = nextFindingIdx(pad);

    set({ analyzeInflightPadId: pad.id, error: null });

    try {
      const res = await fetchWithAuth(
        `${API}/sessions/${encodeURIComponent(session.id)}/pads/${encodeURIComponent(pad.id)}/findings/analyze`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            image_b64: imageB64,
            polygons: [{ idx, wkt: polygonWkt }],
            media_type: mediaType ?? "image/jpeg",
          }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const serverFinding = data.findings?.[0];
      if (!serverFinding || typeof serverFinding.idx !== "number") {
        throw new Error("Malformed analyze response");
      }

      // Reconstruct the local finding shape. Server stored the same
      // polygon_geom (from our WKT) and an initial 'created' history entry.
      // Carry the DB `id` so a freshly-created finding matches a reloaded one —
      // without it, attachments/notes (gated on finding.id) silently disable
      // and id-keyed checks misbehave until the case is reopened.
      const newFinding = {
        id: serverFinding.id,
        idx: serverFinding.idx,
        polygon_geom: polygonGeoJson,
        analysis: serverFinding.analysis,
        history: [createdHistoryEntry()],
      };

      // Append to the pad's findings. Pad status also flips to in_progress
      // server-side; mirror that locally so the UI matches without refetch.
      set((s) => {
        const pads = s.session?.pads ?? [];
        const newPads = pads.map((p, i) =>
          i === padIdx
            ? {
                ...p,
                status: p.status === "pending" ? "in_progress" : p.status,
                findings: [...(p.findings ?? []), newFinding],
                finding_count: (p.finding_count ?? 0) + 1,
              }
            : p
        );
        return {
          session: { ...s.session, pads: newPads },
          analyzeInflightPadId: null,
        };
      });
      return true;
    } catch (err) {
      console.error("[useQcStore] analyzeFinding", err);
      set({
        error: err?.message ?? String(err),
        analyzeInflightPadId: null,
      });
      return false;
    }
  },

  /**
   * Reanalyze an existing finding with an operator correction.
   *
   * Server contract (POST .../findings/:idx/reanalyze):
   *   body: { image_b64, operator_correction, media_type? }
   *   returns: { finding: {idx, analysis, history}, usage, model }
   *
   * Server snapshots prior analysis into history before overwriting, so we
   * just replace the local finding from the response — history grows by 1
   * (the new 'reanalyzed' event with previous_analysis embedded). polygon_geom
   * doesn't change, so we keep the existing one rather than refetching.
   *
   * Per-finding inflight (Set<findingKey>) so parallel reanalyzes on different
   * findings don't trample each other's spinners.
   */
  async reanalyzeFinding({ padIdx, findingIdx, imageB64, operatorCorrection, mediaType }) {
    const state = get();
    const session = state.session;
    if (!session) return false;

    const pad = session.pads?.[padIdx];
    if (!pad) return false;

    const key = findingKey(pad.id, findingIdx);
    if (state.reanalyzeInflight.has(key)) return false;

    set((s) => ({
      reanalyzeInflight: new Set([...s.reanalyzeInflight, key]),
      error: null,
    }));

    try {
      const res = await fetchWithAuth(
        `${API}/sessions/${encodeURIComponent(session.id)}/pads/${encodeURIComponent(pad.id)}/findings/${findingIdx}/reanalyze`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            image_b64: imageB64,
            operator_correction: operatorCorrection,
            media_type: mediaType ?? "image/jpeg",
          }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const updated = data.finding;
      if (!updated || typeof updated.idx !== "number") {
        throw new Error("Malformed reanalyze response");
      }

      // Day 8 follow-on — if the server cleared a dispute as a side-effect
      // of this reanalyze (handler returns dispute_cleared: true), patch
      // the local dispute_* fields so DisputedBanner disappears without
      // waiting for the next full case reload. Server is source of truth:
      // only clear when the response confirms it. If false (finding wasn't
      // disputed), no-op — local state already matches.
      const disputeCleared = data.dispute_cleared === true;

      set((s) => {
        const pads = s.session?.pads ?? [];
        const newPads = pads.map((p, i) => {
          if (i !== padIdx) return p;
          const findings = (p.findings ?? []).map((f) =>
            f.idx === findingIdx
              ? {
                  // Keep existing polygon_geom (unchanged server-side);
                  // analysis + history come from the response.
                  ...f,
                  analysis: updated.analysis,
                  history: updated.history,
                  ...(disputeCleared
                    ? {
                        is_disputed: false,
                        disputed_at: null,
                        disputed_by_user_id: null,
                        disputed_by_org_id: null,
                        disputed_note: null,
                      }
                    : {}),
                }
              : f
          );
          return { ...p, findings };
        });
        const nextSet = new Set(s.reanalyzeInflight);
        nextSet.delete(key);
        return {
          session: { ...s.session, pads: newPads },
          reanalyzeInflight: nextSet,
        };
      });
      return true;
    } catch (err) {
      console.error("[useQcStore] reanalyzeFinding", err);
      set((s) => {
        const nextSet = new Set(s.reanalyzeInflight);
        nextSet.delete(key);
        return {
          error: err?.message ?? String(err),
          reanalyzeInflight: nextSet,
        };
      });
      return false;
    }
  },

  /* ------------------- polygon edit (Day 8 Phase 2) ------------------- */

  /**
   * Enter polygon-edit mode on a specific finding. QcPolygonDrawer
   * subscribes and drives MlDraw (add + enterVertexEdit). The original
   * polygon GeoJSON is captured so Cancel can restore it without a
   * server round-trip.
   */
  enterPolygonEditMode({ findingId, findingIdx, padIdx, originalPolygon }) {
    if (!findingId || originalPolygon == null) return;
    set({
      polygonEditMode: { findingId, findingIdx, padIdx, originalPolygon },
    });
  },

  /**
   * Exit polygon-edit mode. Consumers (QcPolygonDrawer) react to the
   * mode flip and tear down MlDraw. The store just clears the slot.
   */
  exitPolygonEditMode() {
    set({ polygonEditMode: null });
  },

  /**
   * Submit a polygon edit. Calls the extended reanalyze endpoint with
   * the new polygon GeoJSON + correction text. On success, updates the
   * local finding with the server's new analysis + history + polygon_geom
   * and exits polygon-edit mode. Dispute auto-clear is handled the same
   * way as plain reanalyze (Day 8 follow-on).
   *
   * Caller is responsible for the canvas snapshot (same as reanalyzeFinding).
   */
  async submitPolygonEdit({ padIdx, findingIdx, imageB64, operatorCorrection, polygon, mediaType }) {
    const state = get();
    const session = state.session;
    if (!session) return { ok: false, error: "No session" };

    const pad = session.pads?.[padIdx];
    if (!pad) return { ok: false, error: "No pad" };
    if (state.submitPolygonInflight) return { ok: false, error: "Submit in flight" };

    const key = findingKey(pad.id, findingIdx);
    set((s) => ({
      submitPolygonInflight: true,
      reanalyzeInflight: new Set([...s.reanalyzeInflight, key]),
      error: null,
    }));

    try {
      const res = await fetchWithAuth(
        `${API}/sessions/${encodeURIComponent(session.id)}/pads/${encodeURIComponent(pad.id)}/findings/${findingIdx}/reanalyze`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            image_b64: imageB64,
            operator_correction: operatorCorrection ?? "",
            media_type: mediaType ?? "image/jpeg",
            polygon,
          }),
        }
      );
      const data = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        set((s) => {
          const nextSet = new Set(s.reanalyzeInflight);
          nextSet.delete(key);
          return { submitPolygonInflight: false, reanalyzeInflight: nextSet };
        });
        return { ok: false, status: res.status, error: data.error || `HTTP ${res.status}` };
      }
      const updated = data.finding;
      if (!updated || typeof updated.idx !== "number") {
        set((s) => {
          const nextSet = new Set(s.reanalyzeInflight);
          nextSet.delete(key);
          return { submitPolygonInflight: false, reanalyzeInflight: nextSet };
        });
        return { ok: false, error: "Malformed response" };
      }

      const disputeCleared = data.dispute_cleared === true;
      const polygonChanged = data.polygon_changed === true;

      set((s) => {
        const pads = s.session?.pads ?? [];
        const newPads = pads.map((p, i) => {
          if (i !== padIdx) return p;
          const findings = (p.findings ?? []).map((f) =>
            f.idx === findingIdx
              ? {
                  ...f,
                  analysis: updated.analysis,
                  history: updated.history,
                  // polygon_geom from response when polygon_changed; otherwise
                  // keep existing (server didn't write).
                  ...(polygonChanged && updated.polygon_geom
                    ? { polygon_geom: updated.polygon_geom }
                    : {}),
                  ...(disputeCleared
                    ? {
                        is_disputed: false,
                        disputed_at: null,
                        disputed_by_user_id: null,
                        disputed_by_org_id: null,
                        disputed_note: null,
                      }
                    : {}),
                }
              : f
          );
          return { ...p, findings };
        });
        const nextSet = new Set(s.reanalyzeInflight);
        nextSet.delete(key);
        return {
          session: { ...s.session, pads: newPads },
          reanalyzeInflight: nextSet,
          submitPolygonInflight: false,
          polygonEditMode: null,
        };
      });
      return { ok: true, polygon_changed: polygonChanged, dispute_cleared: disputeCleared };
    } catch (err) {
      console.error("[useQcStore] submitPolygonEdit", err);
      set((s) => {
        const nextSet = new Set(s.reanalyzeInflight);
        nextSet.delete(key);
        return { submitPolygonInflight: false, reanalyzeInflight: nextSet };
      });
      return { ok: false, error: err?.message ?? String(err) };
    }
  },

  /**
   * Skip the current pad. PATCH .../status with {status:"skipped"}.
   *
   * Server contract:
   *   200 on pending/in_progress → skipped, returns { pad: {id, status, completed_at} }
   *   409 on terminal-state transitions (skipped/completed → anything)
   *
   * On success: updates pad.status + pad.completed_at locally; advances
   * cursor IF the operator is still on the same pad they triggered Skip
   * from (capture-and-compare guards against mid-flight navigation).
   * On failure: error written to state.error (banner surfaces it).
   * Returns true on success, false on rejection or failure.
   */
  async skipPad({ padIdx }) {
    const state = get();
    const session = state.session;
    if (!session) return false;

    const pad = session.pads?.[padIdx];
    if (!pad) return false;

    // Three operations, one-at-a-time per pad.
    if (state.skipInflightPadId   === pad.id) return false;
    if (state.finishInflightPadId === pad.id) return false;
    if (state.analyzeInflightPadId === pad.id) return false;

    set({ skipInflightPadId: pad.id, error: null });

    try {
      const res = await fetchWithAuth(
        `${API}/sessions/${encodeURIComponent(session.id)}/pads/${encodeURIComponent(pad.id)}/status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "skipped" }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const updatedPad = data.pad;
      if (!updatedPad) throw new Error("Malformed skip response");

      set((s) => {
        const pads = s.session?.pads ?? [];
        const newPads = pads.map((p, i) =>
          i === padIdx
            ? { ...p, status: updatedPad.status, completed_at: updatedPad.completed_at }
            : p
        );
        const shouldAdvance =
          s.currentPadIdx === padIdx && padIdx + 1 < newPads.length;
        return {
          session: { ...s.session, pads: newPads },
          currentPadIdx: shouldAdvance ? padIdx + 1 : s.currentPadIdx,
          skipInflightPadId: null,
        };
      });
      return true;
    } catch (err) {
      console.error("[useQcStore] skipPad", err);
      set({
        error: err?.message ?? String(err),
        skipInflightPadId: null,
      });
      return false;
    }
  },

  /**
   * Finish the current pad. POST .../summarize (AI work).
   *
   * Server contract:
   *   200 on pending/in_progress → completed, returns { summary, usage, model }
   *   409 on terminal-state transitions
   *   502 on AI failure
   *
   * Server response doesn't include the updated pad shape (just the
   * summary), so we fabricate pad.completed_at locally with the client
   * clock. Drift is acceptable — loadSession re-pulls authoritative state.
   *
   * Cursor advance follows the same capture-and-compare rule as skipPad
   * so a mid-AI-call navigation isn't overridden.
   */
  async finishPad({ padIdx }) {
    const state = get();
    const session = state.session;
    if (!session) return false;

    const pad = session.pads?.[padIdx];
    if (!pad) return false;

    if (state.skipInflightPadId   === pad.id) return false;
    if (state.finishInflightPadId === pad.id) return false;
    if (state.analyzeInflightPadId === pad.id) return false;

    set({ finishInflightPadId: pad.id, error: null });

    try {
      const res = await fetchWithAuth(
        `${API}/sessions/${encodeURIComponent(session.id)}/pads/${encodeURIComponent(pad.id)}/summarize`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (!data.summary) throw new Error("Malformed summarize response");

      set((s) => {
        const pads = s.session?.pads ?? [];
        const newPads = pads.map((p, i) =>
          i === padIdx
            ? {
                ...p,
                status: "completed",
                completed_at: new Date().toISOString(),
                summary: data.summary,
              }
            : p
        );
        const shouldAdvance =
          s.currentPadIdx === padIdx && padIdx + 1 < newPads.length;
        return {
          session: { ...s.session, pads: newPads },
          currentPadIdx: shouldAdvance ? padIdx + 1 : s.currentPadIdx,
          finishInflightPadId: null,
        };
      });
      return true;
    } catch (err) {
      console.error("[useQcStore] finishPad", err);
      set({
        error: err?.message ?? String(err),
        finishInflightPadId: null,
      });
      return false;
    }
  },

  /**
   * Export the session as a JSON report and trigger a browser download.
   *
   * Server contract (POST /sessions/:id/export):
   *   200 → returns the report payload as JSON. Also marks the session
   *         `status='completed'` if it was still `active`.
   *   403/404/500 → error path
   *
   * Mechanics: fetch the payload, stringify to a Blob, create an object
   * URL, click an invisible anchor, revoke the URL. Standard pattern;
   * works around the fact that fetch() doesn't honor Content-Disposition
   * for auto-download.
   *
   * Filename matches the server's derivation EXACTLY so the right-click
   * "save as" filename and the click-download filename are identical:
   *   qc_${session.name|replace([^A-Za-z0-9._-]+, _).slice(0,60)}_${YYYYMMDD}.json
   *
   * Errors surface via the shared `error` field — same banner pattern
   * as analyze/skip/finish.
   */
  async exportSession() {
    const state = get();
    const session = state.session;
    if (!session) return false;
    if (state.exportInflight) return false;

    set({ exportInflight: true, error: null });

    try {
      const res = await fetchWithAuth(
        `${API}/sessions/${encodeURIComponent(session.id)}/export`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();

      // Filename derivation — VERBATIM match to admin-api/routes/
      // padrekko-qc.mjs:871-875 so both download paths agree.
      const ymd = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const safeName = String(session.name || "session")
        .replace(/[^A-Za-z0-9._-]+/g, "_")
        .slice(0, 60);
      const filename = `qc_${safeName}_${ymd}.json`;

      // Blob → object URL → anchor click → revoke.
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      set({ exportInflight: false });
      return true;
    } catch (err) {
      console.error("[useQcStore] exportSession", err);
      set({
        error: err?.message ?? String(err),
        exportInflight: false,
      });
      return false;
    }
  },

  /**
   * Record that a finding has been SEEN, or clear that record.
   *
   * Seen is attention, not judgment: it says someone looked, and nothing about
   * whether they agreed. Affirming that the analysis stands is a separate,
   * explicit, never-automatic action — see the /validate endpoint.
   *
   * Persists seen_at server-side (stage writer only) and updates the loaded
   * session.
   */
  /**
   * Affirm that a finding's analysis stands, as it currently reads.
   *
   * The opposite of markFindingSeen in every way that matters: never
   * automatic, and bound to the exact analysis and polygon on screen at the
   * moment of the click. Reanalysing afterwards does not erase it — the
   * affirmation stays in the record and simply reads as superseded.
   *
   * No optimistic update. The server decides what the validation state is —
   * currency is derived from a content hash, and guessing it here would mean a
   * second derivation path in the browser that could disagree with the record.
   * The card renders from the response.
   */
  async validateFinding(padId, idx, note) {
    const sessionId = get().session?.id;
    if (!sessionId) return { ok: false };
    if (get().validateInflightKey) return { ok: false };
    set({ validateInflightKey: findingKey(padId, idx) });
    try {
      const body = note && note.trim() ? { note: note.trim() } : {};
      const res = await fetchWithAuth(
        `${API}/sessions/${encodeURIComponent(sessionId)}/pads/${encodeURIComponent(padId)}/findings/${idx}/validate`,
        { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
      );
      if (!res.ok) {
        const b = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
        throw new Error(b.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      set((s) => {
        if (!s.session) return { validateInflightKey: null };
        const pads = s.session.pads.map((p) =>
          p.id !== padId
            ? p
            : {
                ...p,
                findings: (p.findings || []).map((f) =>
                  f.idx === idx
                    ? { ...f, validation: data.validation, history: data.history ?? f.history }
                    : f
                ),
              }
        );
        return { session: { ...s.session, pads }, validateInflightKey: null };
      });
      return { ok: true, created: data.created === true };
    } catch (err) {
      console.error("[useQcStore] validateFinding", err);
      set({ validateInflightKey: null });
      return { ok: false, error: err?.message };
    }
  },

  async markFindingSeen(padId, idx, seen = true) {
    const sessionId = get().session?.id;
    if (!sessionId) return { ok: false };
    try {
      const res = await fetchWithAuth(
        `${API}/sessions/${encodeURIComponent(sessionId)}/pads/${encodeURIComponent(padId)}/findings/${idx}/seen`,
        { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ seen }) }
      );
      if (!res.ok) {
        const b = await res.json().catch((e) => { console.error("[ui/src/stores/useQcStore.js] res.json() failed (swallowed):", e); return {}; });
        throw new Error(b.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      set((s) => {
        if (!s.session) return {};
        const pads = s.session.pads.map((p) =>
          p.id !== padId
            ? p
            : { ...p, findings: (p.findings || []).map((f) => (f.idx === idx ? { ...f, seen_at: data.seen_at } : f)) }
        );
        return { session: { ...s.session, pads } };
      });
      return { ok: true };
    } catch (err) {
      console.error("[useQcStore] markFindingSeen", err);
      return { ok: false, error: err?.message };
    }
  },

  /**
   * Hide a finding's card from the list. The finding itself stays in session
   * state and on the server; only this client's view of it changes, and the
   * hiding is lost on refresh.
   *
   * Not client-only, despite the above: dismissing a finding means you looked
   * at it, so it also records seen_at. That auto-set is correct and is the
   * whole reason the field could not keep being called "reviewed" — clicking
   * dismiss is not a judgment that the analysis stands.
   */
  dismissFinding(padId, idx) {
    set((s) => ({
      dismissedFindingKeys: new Set([...s.dismissedFindingKeys, findingKey(padId, idx)]),
    }));
    // Stage writer only. Fire-and-forget: a failed stamp must not block the
    // card from hiding.
    if (get().caseWriteable) get().markFindingSeen(padId, idx, true).catch((e) => { console.error("[ui/src/stores/useQcStore.js] markFindingSeen(padId, idx, true) failed (swallowed):", e); });
  },

  /**
   * Toggle map-polygon visibility for one finding. Symmetric add/remove,
   * unlike dismissFinding's one-way add. Card never disappears from the
   * panel — only the polygon on the map flips.
   */
  toggleFindingVisibility(padId, idx) {
    const key = findingKey(padId, idx);
    set((s) => {
      const next = new Set(s.hiddenFindingKeys);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { hiddenFindingKeys: next };
    });
  },

  /**
   * Open the inline correction UI on a specific finding card. At-most-
   * one semantics: opening one card auto-closes whichever was open.
   * Card-local textarea contents are NOT stored here — the receiving
   * card resets its local text on close.
   */
  openCorrect(key) {
    set({ openCorrectFindingKey: key });
  },
  closeCorrect() {
    set({ openCorrectFindingKey: null });
  },

  /**
   * Ask the findings layer to fit-bounds on this finding. {key, nonce}
   * shape — same finding clicked twice still re-fires the layer effect
   * because the object reference changes.
   */
  zoomToFinding(padId, idx) {
    set((s) => ({
      zoomToPolygon: {
        key: findingKey(padId, idx),
        nonce: (s.zoomToPolygon?.nonce ?? 0) + 1,
      },
    }));
  },

  clearSession() {
    set({
      // Case wrapper
      caseInfo: null,
      caseRole: null,
      caseWriteable: false,
      advanceStageInflight: false,
      addParticipantInflight: false,
      reverseStageInflight: false,
      clearDisputeInflightFindingId: null,
        validateInflightKey: null,
      kickbackInflightFindingId: null,
      resubmitInflightFindingId: null,
      reopenInflight: false,
      closeInflight: false,
      sendBackToOperatorInflight: false,
      // Session
      session: null,
      currentPadIdx: null,
      error: null,
      drawingPolygon: false,
      analyzeInflightPadId: null,
      skipInflightPadId: null,
      finishInflightPadId: null,
      exportInflight: false,
      reanalyzeInflight: new Set(),
      dismissedFindingKeys: new Set(),
      hiddenFindingKeys: new Set(),
      zoomToPolygon: null,
      openCorrectFindingKey: null,
      notesByCase: new Map(),
      caseNoteInflight: false,
      findingNoteInflightFindingId: null,
      editNoteInflightId: null,
      deleteNoteInflightId: null,
  // PadRekko attachments — the current case's live (non-deleted) rows plus
  // upload/delete inflight slots. Loaded alongside the case; reset on clear.
  attachments: [],
  attachmentUploadInflight: false,
  attachmentDeleteInflightId: null,
  annotationSaveInflightId: null,
  // PadRekko persistent map measurements (distance/area), case-scoped.
  mapAnnotations: [],
  mapAnnotationSaveInflight: false,
  mapAnnotationDeleteInflightId: null,
  // Operator completion status (per finding) inflight slot.
  findingCompletionInflightId: null,
      polygonEditMode: null,
      submitPolygonInflight: false,
    });
  },

  clearAll() {
    set({
      // Case wrapper
      caseInfo: null,
      caseRole: null,
      caseWriteable: false,
      advanceStageInflight: false,
      // Cases list
      casesList: null,
      casesListLoading: false,
      casesListError: null,
  // Compliance obligations (Day 6) — derived cross-case report + edit inflight.
  obligations: null,
  obligationsLoading: false,
  obligationsError: null,
  nextObsSaveInflight: false,
      // Session
      session: null, currentPadIdx: null, loading: false, error: null,
      drawingPolygon: false,
      analyzeInflightPadId: null,
      skipInflightPadId: null,
      finishInflightPadId: null,
      exportInflight: false,
      reanalyzeInflight: new Set(),
      dismissedFindingKeys: new Set(),
      hiddenFindingKeys: new Set(),
      zoomToPolygon: null,
      openCorrectFindingKey: null,
      notesByCase: new Map(),
      caseNoteInflight: false,
      findingNoteInflightFindingId: null,
      editNoteInflightId: null,
      deleteNoteInflightId: null,
  // PadRekko attachments — the current case's live (non-deleted) rows plus
  // upload/delete inflight slots. Loaded alongside the case; reset on clear.
  attachments: [],
  attachmentUploadInflight: false,
  attachmentDeleteInflightId: null,
  annotationSaveInflightId: null,
  // PadRekko persistent map measurements (distance/area), case-scoped.
  mapAnnotations: [],
  mapAnnotationSaveInflight: false,
  mapAnnotationDeleteInflightId: null,
  // Operator completion status (per finding) inflight slot.
  findingCompletionInflightId: null,
      polygonEditMode: null,
      submitPolygonInflight: false,
    });
  },

  /**
   * Wipe all user-scoped state. Registered with userBoundary so the
   * incoming user doesn't see the previous user's cases / case state /
   * notes / in-flight slots. Pure setState to USER_SCOPED_INITIAL.
   */
  _clearUserScoped: () => set({ ...USER_SCOPED_INITIAL }),
}));

// Register with the user-boundary mechanism so an in-tab user-switch
// (impersonation, hot-swap of identity for testing) flushes per-user
// state before the new user's components fetch. Logout-then-login
// goes through KC redirect + page reload and starts fresh anyway.
import { registerUserBoundaryReset } from "../auth/userBoundary";
registerUserBoundaryReset(
  () => useQcStore.getState()._clearUserScoped(),
  () => useQcStore.getState().listCases(),
);

// Clear any OPEN case when the active project changes, so a case from another
// project doesn't linger after a project switch — including one made from a
// different product mode (e.g. Explore), where QcPicker (which refetches the
// list on project change) isn't mounted. Module-level subscription so it fires
// regardless of which view is currently rendered. The user lands back on the
// project-filtered picker.
useProjectStore.subscribe((state, prev) => {
  if (state.activeProjectId !== prev.activeProjectId && useQcStore.getState().session) {
    useQcStore.getState().clearSession();
  }
});
